import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Phoneboard,
  DashboardStats,
  User,
  WebSocketMessage
} from '@/shared/lib/types';

export { useAuthStore } from './authStore';

export interface AppState {
  // User state
  user: User | null;
  isAuthenticated: boolean;

  // Dashboard stats
  stats: DashboardStats | null;

  // Phoneboards
  phoneboards: Phoneboard[];
  selectedPhoneboard: string | null;

  // UI state
  sidebarCollapsed: boolean;
  loading: {
    phoneboards: boolean;
    stats: boolean;
  };

  // WebSocket connection state
  wsConnected: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setStats: (stats: DashboardStats) => void;
  setPhoneboards: (phoneboards: Phoneboard[]) => void;
  updatePhoneboard: (phoneboardId: string, updates: Partial<Phoneboard>) => void;
  setSelectedPhoneboard: (phoneboardId: string | null) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setLoading: (key: keyof AppState['loading'], loading: boolean) => void;
  setWsConnected: (connected: boolean) => void;
  handleWebSocketMessage: (message: WebSocketMessage) => void;
  reset: () => void;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  stats: null,
  phoneboards: [],
  selectedPhoneboard: null,
  sidebarCollapsed: true, // Mobile-first: start collapsed
  loading: {
    phoneboards: false,
    stats: false,
  },
  wsConnected: false,
};

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setStats: (stats) => set({ stats }),

      setPhoneboards: (phoneboards) => set({ phoneboards }),

      updatePhoneboard: (phoneboardId, updates) =>
        set((state) => ({
          phoneboards: state.phoneboards.map((pb) =>
            pb.id === phoneboardId ? { ...pb, ...updates } : pb
          ),
        })),

      setSelectedPhoneboard: (phoneboardId) => set({ selectedPhoneboard: phoneboardId }),

      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

      setLoading: (key, loading) =>
        set((state) => ({
          loading: { ...state.loading, [key]: loading },
        })),

      setWsConnected: (wsConnected) => set({ wsConnected }),

      handleWebSocketMessage: (message) => {
        const { type, payload } = message;
        const state = get();

        switch (type) {
          case 'phoneboard_update':
            state.updatePhoneboard(payload.id, payload);
            break;
          case 'stats_update':
            state.setStats(payload);
            break;
          default:
            break;
        }
      },

      reset: () => set(initialState),
    }),
    {
      name: 'phoneboard-store',
    }
  )
);